import { getConversionRate } from './currencyConvert';

/**
 * Derived metrics computation for the Property Portfolio page.
 *
 * Pure function: takes the raw dataset from GET /reports/portfolio/dataset
 * and returns all aggregations needed by every view (KPIs, charts, tables).
 *
 * Cost classification is performed by the backend (classify_booking_pnl),
 * shared with the P&L endpoint. The frontend consumes the pre-classified
 * cost buckets (ota_commission, management_fees, operational, financing,
 * unclassified) and never re-derives costs from raw booking fields.
 *
 * Reconciliation invariant: YTD totals === sum of monthly entries.
 * All aggregation flows from a single source (per-property monthly buckets):
 *   backend pnl_by_month -> per-property monthly -> per-property YTD (sum of monthly)
 *                                                -> region monthly (sum of per-property monthly)
 *                                                -> region totals (sum of region monthly)
 *
 * Booking-to-month assignment: each booking is assigned to its check-in month
 * for revenue, margin, and booking-count aggregation.
 *
 * Nightly rate uses stay-month attribution: the backend prorates revenue and
 * nights across the actual months a booking spans (overlap/night-split model,
 * same population as the occupancy chart). The frontend reads per-property
 * stay_month_nights and stay_month_revenue from the dataset and uses those
 * for avg_nightly_rate at every aggregation level.
 *
 * No React, no side effects.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function computeDerivedMetrics(dataset) {
  if (!dataset) return null;

  const rawBookings = dataset.bookings || [];
  const rawProperties = dataset.properties || [];
  const recurring_costs = dataset.recurring_costs || [];
  const investments = dataset.investments || [];
  const fx_rates = dataset.fx_rates;
  const eurGbp = parseFloat(fx_rates?.EUR_GBP) || 0;
  if (eurGbp === 0) {
    console.warn('portfolioDerived: EUR_GBP rate is 0 or missing; EUR values will show as 0 in GBP mode');
  }

  // Region-level unattributed cost adjustments from the backend (parity
  // with P&L endpoint). These are entity-level cleaning/commission costs
  // that cannot be attributed to a specific property. The P&L endpoint
  // includes them in its region totals; we apply them here so the
  // dashboard Net Margin matches the P&L Net Result.
  const regionCostAdj = dataset.region_cost_adjustments || {};

  // Company overheads (payroll + subscriptions) per region and month, in the
  // region's own currency (UK GBP, France EUR), from the backend
  // (services/overheads.py). Margin tiers (Fan, 2026-11-04, A148):
  //   net revenue  = gross - OTA commission - management fees - cleaning - repairs
  //   gross margin = net revenue - fixed property costs (rent, mortgage,
  //                  utilities, taxes, insurance, service charge, other)
  //                = gross - every property cost
  //   net margin   = gross margin - overheads
  // A property has no overheads of its own: per property, net margin equals
  // gross margin; overheads come off at region and portfolio level.
  const overheadsByRegion = dataset.overheads_by_region || {};
  const overheadOf = (region, mk) => parseFloat((overheadsByRegion[region] || {})[mk]) || 0;

  // Property lookup
  const propertyMap = {};
  for (const p of rawProperties) {
    propertyMap[p.property_id] = p;
  }

  // Recurring costs by property|month (P&L-eligible only for margin)
  // Kept for the Costs page overview; the portfolio table now reads
  // cost buckets from the backend pnl/pnl_by_month instead.
  const costsByPropMonth = {};
  const allCostsByPropMonth = {};
  for (const c of recurring_costs) {
    const key = `${c.property_id}|${c.month}`;
    const amt = parseFloat(c.amount) || 0;
    allCostsByPropMonth[key] = (allCostsByPropMonth[key] || 0) + amt;
    // Only pnl_eligible costs reduce margin (interest yes, principal no)
    if (c.pnl_eligible !== false) {
      costsByPropMonth[key] = (costsByPropMonth[key] || 0) + amt;
    }
  }

  // Investment by property
  const investmentByProp = {};
  const investmentCurrencyByProp = {};
  for (const inv of investments) {
    const total = (parseFloat(inv.purchase_price) || 0) + (parseFloat(inv.acquisition_costs) || 0);
    investmentByProp[inv.property_id] = (investmentByProp[inv.property_id] || 0) + total;
    if (!investmentCurrencyByProp[inv.property_id]) {
      investmentCurrencyByProp[inv.property_id] = inv.currency;
    }
  }

  // ── Enrich bookings ──
  // Each booking is assigned to its check-in month for all aggregation.
  const bookings = rawBookings.map(b => {
    const prop = propertyMap[b.property_id];
    const currency = prop?.currency || 'GBP';
    const region = prop?.region || 'uk';

    const revenue = parseFloat(b.revenue) || 0;
    const gross_revenue = parseFloat(b.gross_revenue) || revenue;

    // net_revenue comes directly from the backend (post-commission).
    // Do NOT re-derive as revenue - channel_commission (double-deduction bug).
    const net_revenue = parseFloat(b.net_revenue) || revenue;
    // Guest-paid total (what the management statement calls gross) --
    // distinct from gross_revenue, which for a managed booking is the
    // owner's fee revenue, not the guest's total. null when the backend
    // couldn't resolve it (e.g. a quarantined total_price).
    const guest_total = b.guest_total != null ? parseFloat(b.guest_total) : null;
    // Guest-basis commission (the channel's cut of guest_total), converted
    // together with guest_total on the backend so the two are always present
    // or absent together. Distinct from ota_commission below, which is
    // zeroed for MANAGED bookings on purpose (P&L basis).
    const guest_commission = b.guest_commission != null ? parseFloat(b.guest_commission) : null;
    // Management statement's own per-booking gross/commission -- COMPARATOR
    // only, never the display basis (see display_gross below). Lets the
    // frontend show "platform vs statement" on a ref-matched managed row
    // whose mgmt_statement_status is ref_matched_amount_mismatch. Both
    // fields come from the same statement row so they always reconcile
    // with each other.
    const statement_gross = b.statement_gross != null ? parseFloat(b.statement_gross) : null;
    const statement_commission = b.statement_commission != null ? parseFloat(b.statement_commission) : null;
    // Booking-level OTA commission (the only booking-driven cost bucket in
    // this pipeline -- see the actuals-gating comment below): parsed here,
    // alongside revenue, so it's available to the same check_in<today gate.
    const ota_commission = parseFloat(b.ota_commission) || 0;
    // Bookings-tab display basis = guest_total, ALWAYS -- the platform
    // (Hostaway/Smoobu) is the independent source of what the guest paid;
    // the management statement is what's being audited against it, so it
    // is never substituted in here (that would make the audit circular --
    // see statement_gross above). When the platform can't resolve a guest
    // total (guest_total null, e.g. a quarantined total_price), display_gross
    // is null and the row must render an explicit "guest total unavailable"
    // marker -- never a silent net/fee-basis number and never the statement
    // figure standing in as if it were the platform's own.
    const display_gross = guest_total;
    const display_commission = guest_total != null ? (guest_commission || 0) : null;
    const display_net = guest_total != null ? (display_gross - display_commission) : null;
    const toGbp = currency === 'GBP' ? 1 : (currency === 'EUR' ? eurGbp : 0);
    const revenue_gbp = revenue * toGbp;
    const check_in_month = b.check_in ? b.check_in.substring(0, 7) : null;

    return {
      ...b,
      currency,
      region,
      property_name: prop?.name || b.property_id,
      gross_revenue,
      guest_total,
      guest_commission,
      statement_gross,
      statement_commission,
      net_revenue,
      ota_commission,
      display_gross,
      display_commission,
      display_net,
      revenue_gbp,
      check_in_month,
    };
  });

  // ── Per-property: monthly first, then YTD from monthly sums ──
  const today = new Date();
  const currentYear = dataset.year;

  // UTC month key, same basis as currentMonthKey below.
  const currentMonthKeyUtc = currentYear === today.getUTCFullYear()
    ? today.toISOString().slice(0, 7)
    : `${currentYear}-12`;

  const propertiesYtd = rawProperties.map(prop => {
    const pid = prop.property_id;
    const propBookings = bookings.filter(b => b.property_id === pid);
    const currency = prop.currency || 'GBP';
    const region = prop.region;

    // Backend-provided P&L buckets per month (from classify_booking_pnl +
    // recurring cost classification, shared with the P&L endpoint).
    const pnlByMonth = prop.pnl_by_month || {};

    // Build monthly breakdown FIRST (this is the single source of truth)
    // Stay-month dicts from the backend: prorated revenue/nights for nightly rate
    const stayNightsMap = prop.stay_month_nights || {};
    const stayRevenueMap = prop.stay_month_revenue || {};
    const availableNightsMap = prop.available_nights_by_month || {};

    const monthly = [];
    for (let m = 1; m <= 12; m++) {
      const mk = `${currentYear}-${String(m).padStart(2, '0')}`;
      const monthBookings = propBookings.filter(b => b.check_in_month === mk);
      // A cancelled booking (even a paid one, whose fee-only revenue still
      // flows through mGrossRev/mOta below via the backend's pnl_by_month)
      // never stayed and must not count as a booking.
      const monthBookingsCount = monthBookings.filter(b => !b.is_cancelled).length;
      const mNights = monthBookings.reduce((s, b) => s + (b.nights || 0), 0);

      // Cost buckets from the backend (single source of truth).
      // A month AFTER the current one has no bookings yet (the dataset stops
      // at this month), only its scheduled recurring costs. Counting those
      // made every YTD margin (cards, chart footers, region tabs) subtract
      // November and December costs with no revenue against them (Fan,
      // 2026-10-25: dashboard UK net margin 295k vs P&L 348k). They are kept
      // aside as scheduled_costs so the forecast margin can deduct them.
      const isFutureMonth = mk > currentMonthKeyUtc;
      const mPnlRaw = pnlByMonth[mk] || {};
      const mScheduledCosts = isFutureMonth ? (parseFloat(mPnlRaw.total_costs) || 0) : 0;
      const mPnl = isFutureMonth ? {} : mPnlRaw;
      const mGrossRev = parseFloat(mPnl.gross_revenue) || 0;
      const mOta = parseFloat(mPnl.ota_commission) || 0;
      // net_revenue from backend: gross - OTA, computed once on the server.
      const mNetRevenue = parseFloat(mPnl.net_revenue) || (mGrossRev - mOta);
      const mMgmtFees = parseFloat(mPnl.management_fees) || 0;
      const mOperational = parseFloat(mPnl.operational) || 0;
      const mCleaning = parseFloat(mPnl.cleaning) || 0;
      const mRepairs = parseFloat(mPnl.repairs) || 0;
      const mOtherOperational = parseFloat(mPnl.other_operational) || 0;
      const mFinancing = parseFloat(mPnl.financing) || 0;
      const mUnclassified = parseFloat(mPnl.unclassified) || 0;
      const mTotalCosts = parseFloat(mPnl.total_costs) || 0;
      const mNetResult = parseFloat(mPnl.net_result) || 0;

      // Stay-month attribution for nightly rate (converged with occupancy path)
      const stayNights = stayNightsMap[mk] || 0;
      const stayRevenue = parseFloat(stayRevenueMap[mk]) || 0;
      const availableNights = availableNightsMap[mk] || 0;

      // revenue = revenue after OTA commission (gross - OTA), for the
      // booking-revenue charts and heatmaps. Net Revenue is the A148 tier:
      // also after management fees, cleaning and repairs.
      const mRevenue = mNetRevenue;
      const mNetRevenueTier = mGrossRev - mOta - mMgmtFees - mCleaning - mRepairs;

      // ADR = stay revenue / nights sold; RevPAR = stay revenue / available nights
      const adr = stayNights > 0 ? stayRevenue / stayNights : 0;
      const revpar = availableNights > 0 ? stayRevenue / availableNights : 0;

      monthly.push({
        month: mk,
        label: MONTHS[m - 1],
        revenue: mRevenue,
        net_revenue: mNetRevenueTier,
        gross_revenue: mGrossRev,
        ota_commission: mOta,
        management_fees: mMgmtFees,
        operational: mOperational,
        cleaning: mCleaning,
        repairs: mRepairs,
        other_operational: mOtherOperational,
        financing: mFinancing,
        unclassified: mUnclassified,
        total_costs: mTotalCosts,
        net_margin: mNetResult,
        margin_pct: mGrossRev > 0 ? ((mNetResult / mGrossRev) * 100) : 0,
        gross_margin: mNetResult,
        occupancy: (prop.occupancy_by_month || {})[mk] || 0,
        avg_nightly_rate: adr,
        adr,
        revpar,
        available_nights: availableNights,
        bookings: monthBookingsCount,
        nights: mNights,
        // Stay-month fields for upstream aggregation (nightly rate chart)
        stay_nights: stayNights,
        stay_revenue: stayRevenue,
        scheduled_costs: mScheduledCosts,
      });
    }

    // Derive YTD by summing monthly entries (reconciliation by construction)
    const revenue_native = monthly.reduce((s, m) => s + m.revenue, 0);
    const net_revenue_native = monthly.reduce((s, m) => s + m.net_revenue, 0);
    const gross_revenue_native = monthly.reduce((s, m) => s + m.gross_revenue, 0);
    const ota_commission_native = monthly.reduce((s, m) => s + m.ota_commission, 0);
    const management_fees_native = monthly.reduce((s, m) => s + m.management_fees, 0);
    const operational_native = monthly.reduce((s, m) => s + m.operational, 0);
    const cleaning_native = monthly.reduce((s, m) => s + m.cleaning, 0);
    const repairs_native = monthly.reduce((s, m) => s + m.repairs, 0);
    const other_operational_native = monthly.reduce((s, m) => s + m.other_operational, 0);
    const financing_native = monthly.reduce((s, m) => s + m.financing, 0);
    const unclassified_native = monthly.reduce((s, m) => s + m.unclassified, 0);
    const total_costs_native = monthly.reduce((s, m) => s + m.total_costs, 0);
    const net_margin_native = monthly.reduce((s, m) => s + m.net_margin, 0);
    const gross_margin_native = net_margin_native;
    const bookings_count = monthly.reduce((s, m) => s + m.bookings, 0);
    const nights = monthly.reduce((s, m) => s + m.nights, 0);
    const margin_pct = gross_revenue_native > 0 ? ((net_margin_native / gross_revenue_native) * 100) : 0;
    const gross_margin_pct = gross_revenue_native > 0 ? ((gross_margin_native / gross_revenue_native) * 100) : 0;
    // Nightly rate YTD: stay-month attribution (converged with occupancy)
    const stay_nights_ytd = monthly.reduce((s, m) => s + m.stay_nights, 0);
    const stay_revenue_ytd = monthly.reduce((s, m) => s + m.stay_revenue, 0);
    const available_nights_ytd = monthly.reduce((s, m) => s + m.available_nights, 0);
    const avg_nightly_rate_native = stay_nights_ytd > 0 ? stay_revenue_ytd / stay_nights_ytd : 0;
    // ADR = stay revenue / nights sold (same as avg_nightly_rate_native)
    const adr_native = avg_nightly_rate_native;
    // RevPAR (calendar basis) = stay revenue / available nights
    const revpar_native = available_nights_ytd > 0 ? stay_revenue_ytd / available_nights_ytd : 0;

    // Occupancy YTD
    const occupancy_by_month = prop.occupancy_by_month || {};
    let totalOccDays = 0;
    let totalDays = 0;
    const maxMonth = currentYear === today.getFullYear() ? today.getMonth() + 1 : 12;
    for (let m = 1; m <= maxMonth; m++) {
      const mk = `${currentYear}-${String(m).padStart(2, '0')}`;
      const daysInMonth = new Date(currentYear, m, 0).getDate();
      const occ = occupancy_by_month[mk] || 0;
      totalOccDays += (occ / 100) * daysInMonth;
      totalDays += daysInMonth;
    }
    const occupancy_ytd_pct = totalDays > 0 ? (totalOccDays / totalDays) * 100 : 0;

    // Investment and ROI
    const investment_native = investmentByProp[pid] || null;
    const investment_currency = investmentCurrencyByProp[pid] || currency;

    let roi_pct = null;
    if (investment_native && investment_native > 0) {
      const yearStart = `${currentYear}-01-01`;
      const startDate = prop.first_listed_date && prop.first_listed_date > yearStart
        ? prop.first_listed_date : yearStart;
      const todayStr = today.toISOString().split('T')[0];
      const yearEnd = `${currentYear}-12-31`;
      const endDate = yearEnd < todayStr ? yearEnd : todayStr;

      const startD = new Date(startDate);
      const endD = new Date(endDate);
      const months_elapsed = (endD.getFullYear() - startD.getFullYear()) * 12
        + (endD.getMonth() - startD.getMonth());

      if (months_elapsed > 0) {
        const annualised_net_margin = (net_margin_native / months_elapsed) * 12;
        let investment_converted = investment_native;
        if (investment_currency !== currency) {
          if (investment_currency === 'EUR' && currency === 'GBP') {
            investment_converted = investment_native * eurGbp;
          } else if (investment_currency === 'GBP' && currency === 'EUR' && eurGbp > 0) {
            investment_converted = investment_native / eurGbp;
          }
        }
        if (investment_converted > 0) {
          roi_pct = (annualised_net_margin / investment_converted) * 100;
        }
      }
    }

    return {
      property_id: pid,
      name: prop.name,
      region,
      currency,
      tenure: prop.tenure || null,
      management_type: prop.management_type || 'owned',
      entity_name: prop.entity_id,
      first_listed_date: prop.first_listed_date,
      revenue_native,
      net_revenue_native,
      gross_revenue_native,
      ota_commission_native,
      management_fees_native,
      operational_native,
      cleaning_native,
      repairs_native,
      other_operational_native,
      financing_native,
      unclassified_native,
      total_costs_native,
      net_margin_native,
      gross_margin_native,
      margin_pct,
      gross_margin_pct,
      occupancy_ytd_pct,
      avg_nightly_rate_native,
      adr_native,
      revpar_native,
      bookings_count,
      nights,
      investment_native,
      investment_currency,
      roi_pct,
      yield_pct: roi_pct,
      monthly,
    };
  });

  // ── Current-month actuals/forecast split (forecast-whole-months bug fix) ──
  // /reports/portfolio/dataset already bounds bookings to check_in < first-
  // of-next-month, so future months are correctly all-zero here (their
  // forecast comes from the separate rolling forward-booking endpoint,
  // merged in by the chart builder). The one gap: the CURRENT month renders
  // as a single whole-month actual figure today. Split it into actuals-to-
  // date (check_in < today) and a forecast-styled remainder (check_in >=
  // today, still within the current month) directly from the same raw
  // bookings already loaded above -- no re-derivation of classification,
  // just re-summing already-classified gross_revenue by date.
  // Both derived from the same UTC string (never today.getFullYear()/
  // getMonth(), which read the browser's LOCAL timezone): during BST, the
  // hour after 23:00 UTC on a month's last day is already local-midnight
  // into the next month, so a local-time currentMonthKey would disagree
  // with the UTC todayStr every booking comparison below is keyed against
  // -- misclassifying an entire month's bookings as forward. Matches the
  // backend reference's basis (entity_pnl.py: datetime.now(timezone.utc)).
  const todayStr = today.toISOString().slice(0, 10);
  const currentMonthKey = todayStr.slice(0, 7);
  const currentMonthActualForward = {
    uk: { actual: 0, forward: 0, netActual: 0, netForward: 0, otaActual: 0, otaForward: 0 },
    fr: { actual: 0, forward: 0, netActual: 0, netForward: 0, otaActual: 0, otaForward: 0 },
  };
  for (const b of bookings) {
    if (b.check_in_month !== currentMonthKey) continue;
    const bucket = currentMonthActualForward[b.region];
    if (!bucket) continue;
    if (b.check_in < todayStr) {
      bucket.actual += b.gross_revenue;
      bucket.netActual += b.net_revenue;
      bucket.otaActual += b.ota_commission;
    } else {
      bucket.forward += b.gross_revenue;
      bucket.netForward += b.net_revenue;
      bucket.otaForward += b.ota_commission;
    }
  }

  // ── Monthly by region (derived from per-property monthly, not re-computed) ──
  const monthlyByRegion = { uk: [], fr: [] };
  for (let m = 1; m <= 12; m++) {
    const mk = `${currentYear}-${String(m).padStart(2, '0')}`;
    const mIdx = m - 1;

    for (const regionKey of ['uk', 'fr']) {
      const regionProps = propertiesYtd.filter(p => p.region === regionKey);

      // Sum per-property monthly entries for this region and month
      let grossRev = 0, ota = 0, mgmtFees = 0, operational = 0, cleaning = 0, repairs = 0, otherOp = 0, financing = 0, unclassified = 0, totalCosts = 0;
      let rNights = 0, rBookings = 0;
      let rStayNights = 0, rStayRevenue = 0, rAvailNights = 0;
      let scheduledCosts = 0;
      const regionOccs = [];
      for (const p of regionProps) {
        const pm = p.monthly[mIdx];
        grossRev += pm.gross_revenue;
        ota += pm.ota_commission;
        mgmtFees += pm.management_fees;
        operational += pm.operational;
        cleaning += pm.cleaning || 0;
        repairs += pm.repairs || 0;
        otherOp += pm.other_operational || 0;
        financing += pm.financing || 0;
        unclassified += pm.unclassified || 0;
        totalCosts += pm.total_costs;
        rNights += pm.nights;
        rBookings += pm.bookings;
        rStayNights += pm.stay_nights;
        rStayRevenue += pm.stay_revenue;
        rAvailNights += pm.available_nights || 0;
        scheduledCosts += pm.scheduled_costs || 0;
        regionOccs.push(pm.occupancy);
      }

      // Entity-level unattributed cost adjustments (parity with P&L endpoint).
      // These are entity-level costs (cleaning -> operational, commission ->
      // management_fees) not attributable to a single property.
      // Future months: no actual entity-level cost yet (see isFutureMonth).
      const adjM = mk > currentMonthKeyUtc ? {} : ((regionCostAdj[regionKey] ?? {})[mk] ?? {});
      const adjOp = adjM.operational ?? 0;
      const adjClean = adjM.cleaning ?? 0;
      const adjMgmt = adjM.management_fees ?? 0;
      // Estimated statement costs for actual months whose management
      // statement is not in yet (backend pending_statement_estimates, A134):
      // repairs ride inside operational; `estimated` is the labelled total.
      const adjRepairs = adjM.repairs ?? 0;
      const adjEstimated = adjM.estimated ?? 0;
      operational += adjOp;
      cleaning += adjClean;
      repairs += adjRepairs;
      mgmtFees += adjMgmt;
      totalCosts += adjOp + adjMgmt;

      const regionOverhead = mk > currentMonthKeyUtc ? 0 : overheadOf(regionKey, mk);
      const grossMargin = grossRev - totalCosts;
      const netMargin = grossMargin - regionOverhead;
      const netRevenueTier = grossRev - ota - mgmtFees - cleaning - repairs;
      const avgOcc = regionOccs.length > 0 ? regionOccs.reduce((s, v) => s + v, 0) / regionOccs.length : 0;
      const avgNightlyRate = rStayNights > 0 ? rStayRevenue / rStayNights : 0;
      const regionRevpar = rAvailNights > 0 ? rStayRevenue / rAvailNights : 0;

      const entry = {
        month: mk,
        label: MONTHS[mIdx],
        bookings: rBookings,
        nights: rNights,
        occupancy_pct: avgOcc,
        stay_nights: rStayNights,
        stay_revenue: rStayRevenue,
        available_nights: rAvailNights,
      };

      const ownerRev = grossRev - ota;

      // Actuals/forecast split -- meaningful only for the current month
      // (other months: past is fully actual, future is structurally zero
      // since the dataset query excludes future check-ins already).
      const cmaf = currentMonthActualForward[regionKey];
      const grossRevActualNative = mk === currentMonthKey ? cmaf.actual : grossRev;
      const grossRevForwardNative = mk === currentMonthKey ? cmaf.forward : 0;
      const ownerRevActualNative = mk === currentMonthKey ? cmaf.netActual : ownerRev;
      const ownerRevForwardNative = mk === currentMonthKey ? cmaf.netForward : 0;

      // Margin actuals/forecast split (dashboards round 2, "incl. to come"
      // fix; re-fixed to gate booking-driven costs -- see below): same rule
      // the entity-scope equivalent uses (services/entity_pnl.py's
      // _portfolio_monthly_series, net_result_actual/gross_margin_actual,
      // ~lines 590-611) --
      //   - past month (mk < currentMonthKey): fully actual already (the
      //     dataset query excludes future check-ins, so nothing here could
      //     be forward-looking).
      //   - current month: revenue uses the precise booking-level split
      //     above; costs must be split the SAME way, not treated as a
      //     single whole-month figure -- entity_pnl.py gates its booking-
      //     level cost buckets (ota_actual, operational_booking_actual) by
      //     check_in<today, identical to revenue, and only its genuinely
      //     recurring/property-level buckets (management_fees, financing,
      //     unclassified, and the recurring share of operational) by the
      //     coarser month-boundary. In this pipeline OTA commission is the
      //     ONLY booking-driven cost bucket (routes/portfolio.py's own
      //     comment: "Booking-level operational is always _ZERO -- costs
      //     enter via recurring path"), so excluding the forward portion of
      //     OTA (cmaf.otaForward, same check_in<today gate as revenue,
      //     accumulated above) from totalCosts reproduces the backend
      //     split exactly; every other bucket stays month-gated, matching
      //     entity_pnl.py's own treatment of those buckets. Previously this
      //     subtracted the whole month's totalCosts against only the
      //     actuals-to-date revenue, understating the current month's
      //     margin by the forward bookings' OTA commission (measured: PR
      //     review of the group-vs-entities Margin chart gap, July 2026).
      //   - future month (mk > currentMonthKey): forced to zero regardless
      //     of what raw cost data this dataset happens to carry there --
      //     a future month's net result has not happened yet.
      let netMarginActualNative;
      let grossMarginActualNative;
      let netRevenueActualNative;
      if (mk > currentMonthKey) {
        netMarginActualNative = 0;
        grossMarginActualNative = 0;
        netRevenueActualNative = 0;
      } else if (mk === currentMonthKey) {
        const totalCostsActualNative = totalCosts - cmaf.otaForward;
        grossMarginActualNative = grossRevActualNative - totalCostsActualNative;
        netMarginActualNative = grossMarginActualNative - regionOverhead;
        netRevenueActualNative = grossRevActualNative - (ota - cmaf.otaForward) - mgmtFees - cleaning - repairs;
      } else {
        netMarginActualNative = netMargin;
        grossMarginActualNative = grossMargin;
        netRevenueActualNative = netRevenueTier;
      }

      if (regionKey === 'uk') {
        entry.revenue_gbp = ownerRev;
        entry.net_revenue_gbp = netRevenueTier;
        entry.net_revenue_actual_gbp = netRevenueActualNative;
        entry.overheads_gbp = regionOverhead;
        entry.revenue_actual_gbp = ownerRevActualNative;
        entry.revenue_forward_gbp = ownerRevForwardNative;
        entry.gross_revenue_gbp = grossRev;
        entry.gross_revenue_actual_gbp = grossRevActualNative;
        entry.gross_revenue_forward_gbp = grossRevForwardNative;
        entry.ota_commission_gbp = ota;
        entry.management_fees_gbp = mgmtFees;
        entry.operational_gbp = operational;
        entry.cleaning_gbp = cleaning;
        entry.repairs_gbp = repairs;
        entry.other_operational_gbp = otherOp;
        entry.financing_gbp = financing;
        entry.unclassified_gbp = unclassified;
        entry.total_costs_gbp = totalCosts;
        entry.net_margin_gbp = netMargin;
        entry.gross_margin_gbp = grossMargin;
        entry.net_margin_actual_gbp = netMarginActualNative;
        entry.gross_margin_actual_gbp = grossMarginActualNative;
        entry.avg_nightly_rate_gbp = avgNightlyRate;
        entry.revpar_gbp = regionRevpar;
        // Future months only: recurring costs already scheduled (GBP).
        entry.scheduled_costs_in_gbp = scheduledCosts;
        entry.estimated_statement_costs_gbp = adjEstimated;
      } else {
        entry.estimated_statement_costs_eur = adjEstimated;
        entry.estimated_statement_costs_gbp = adjEstimated * eurGbp;
        entry.revenue_eur = ownerRev;
        entry.net_revenue_eur = netRevenueTier;
        entry.net_revenue_actual_eur = netRevenueActualNative;
        entry.overheads_eur = regionOverhead;
        entry.revenue_actual_eur = ownerRevActualNative;
        entry.revenue_forward_eur = ownerRevForwardNative;
        entry.gross_revenue_eur = grossRev;
        entry.gross_revenue_actual_eur = grossRevActualNative;
        entry.gross_revenue_forward_eur = grossRevForwardNative;
        entry.ota_commission_eur = ota;
        entry.management_fees_eur = mgmtFees;
        entry.operational_eur = operational;
        entry.cleaning_eur = cleaning;
        entry.repairs_eur = repairs;
        entry.other_operational_eur = otherOp;
        entry.financing_eur = financing;
        entry.unclassified_eur = unclassified;
        entry.total_costs_eur = totalCosts;
        entry.net_margin_eur = netMargin;
        entry.gross_margin_eur = grossMargin;
        entry.net_margin_actual_eur = netMarginActualNative;
        entry.gross_margin_actual_eur = grossMarginActualNative;
        entry.avg_nightly_rate_eur = avgNightlyRate;
        entry.revpar_eur = regionRevpar;
        // GBP-converted siblings for cross-currency chart display
        entry.revenue_gbp = ownerRev * eurGbp;
        entry.net_revenue_gbp = netRevenueTier * eurGbp;
        entry.net_revenue_actual_gbp = netRevenueActualNative * eurGbp;
        entry.overheads_gbp = regionOverhead * eurGbp;
        entry.revenue_actual_gbp = ownerRevActualNative * eurGbp;
        entry.revenue_forward_gbp = ownerRevForwardNative * eurGbp;
        entry.gross_revenue_gbp = grossRev * eurGbp;
        entry.gross_revenue_actual_gbp = grossRevActualNative * eurGbp;
        entry.gross_revenue_forward_gbp = grossRevForwardNative * eurGbp;
        entry.ota_commission_gbp = ota * eurGbp;
        entry.management_fees_gbp = mgmtFees * eurGbp;
        entry.operational_gbp = operational * eurGbp;
        entry.cleaning_gbp = cleaning * eurGbp;
        entry.repairs_gbp = repairs * eurGbp;
        entry.other_operational_gbp = otherOp * eurGbp;
        entry.financing_gbp = financing * eurGbp;
        entry.unclassified_gbp = unclassified * eurGbp;
        entry.total_costs_gbp = totalCosts * eurGbp;
        entry.net_margin_gbp = netMargin * eurGbp;
        entry.gross_margin_gbp = grossMargin * eurGbp;
        entry.net_margin_actual_gbp = netMarginActualNative * eurGbp;
        entry.gross_margin_actual_gbp = grossMarginActualNative * eurGbp;
        entry.avg_nightly_rate_gbp = avgNightlyRate * eurGbp;
        entry.revpar_gbp = regionRevpar * eurGbp;
        entry.scheduled_costs_in_gbp = scheduledCosts * eurGbp;
      }

      monthlyByRegion[regionKey].push(entry);
    }
  }

  // ── Actuals-scoped YTD per-region totals (dashboards round 2, "incl. to
  // come" fix) -- summed NATIVELY from monthlyByRegion's own per-region
  // *_actual_native fields (gbp for uk, eur for fr), the SAME predicate the
  // Gross Revenue chart's YTD footer already reads via
  // groupGrossRevenueChart.computeGrossRevenueYtd. Attached onto
  // ukTotals/frTotals below (alongside buildTotals' own fields) so the
  // existing region-conversion machinery (convertRegionTotals, used for the
  // EUR toggle) carries them through correctly -- never a GBP round-trip.
  const _sumActualByRegion = (regionKey, nativeSuffix) => monthlyByRegion[regionKey].reduce((acc, m) => ({
    revenue_actual: acc.revenue_actual + (m[`revenue_actual_${nativeSuffix}`] || 0),
    net_revenue_actual: acc.net_revenue_actual + (m[`net_revenue_actual_${nativeSuffix}`] || 0),
    gross_revenue_actual: acc.gross_revenue_actual + (m[`gross_revenue_actual_${nativeSuffix}`] || 0),
    net_margin_actual: acc.net_margin_actual + (m[`net_margin_actual_${nativeSuffix}`] || 0),
    gross_margin_actual: acc.gross_margin_actual + (m[`gross_margin_actual_${nativeSuffix}`] || 0),
  }), { revenue_actual: 0, net_revenue_actual: 0, gross_revenue_actual: 0, net_margin_actual: 0, gross_margin_actual: 0 });
  const ukActualTotals = _sumActualByRegion('uk', 'gbp');
  const frActualTotals = _sumActualByRegion('fr', 'eur');

  // ── Monthly combined across all regions (for All tab aggregate lines) ──
  const monthlyCombined = [];
  let ytdOccDaysTotal = 0, ytdAvailDaysTotal = 0, ytdGrossRevGbpTotal = 0, ytdRevGbpTotal = 0, ytdNightsTotal = 0;
  let ytdOccDaysFr = 0, ytdAvailDaysFr = 0, ytdGrossRevGbpFr = 0, ytdRevGbpFr = 0, ytdNightsFr = 0;
  let ytdGrossRevNativeFrEur = 0, ytdRevNativeFrEur = 0, ytdNetMarginNativeFrEur = 0, ytdNetMarginGbpFr = 0;
  let ytdOccDaysUk = 0, ytdAvailDaysUk = 0, ytdGrossRevGbpUk = 0, ytdRevGbpUk = 0, ytdNightsUk = 0;
  let ytdNetMarginGbpUk = 0;
  let ytdGrossMarginGbpUk = 0, ytdGrossMarginGbpFr = 0, ytdGrossMarginNativeFrEur = 0;
  // Stay-month YTD accumulators for nightly rate (GBP-converted)
  let ytdStayNightsTotal = 0, ytdStayRevGbpTotal = 0, ytdAvailNightsTotal = 0;
  let ytdStayNightsFr = 0, ytdStayRevGbpFr = 0, ytdAvailNightsFr = 0;
  let ytdStayNightsUk = 0, ytdStayRevGbpUk = 0, ytdAvailNightsUk = 0;
  // Owned-only accumulators for headline RevPAR/ADR
  let ytdOwnedStayNights = 0, ytdOwnedStayRevGbp = 0, ytdOwnedAvailNights = 0;
  // Per-region owned-only accumulators for rate card UK/FR sub-lines
  let ytdOwnedStayNightsUk = 0, ytdOwnedStayRevGbpUk = 0, ytdOwnedAvailNightsUk = 0;
  let ytdOwnedStayNightsFr = 0, ytdOwnedStayRevGbpFr = 0, ytdOwnedAvailNightsFr = 0;
  let ytdOwnedStayRevNativeFrEur = 0;
  // The backend dataset endpoint already filters check_in <= today, so the
  // frontend has only past/present bookings. Sum all 12 months so YTD totals
  // agree with the KPI totals (which also sum all 12 months).
  // Occupancy is a time-weighted average: future months read as 0% because they
  // haven't happened, not because occupancy is genuinely zero. Truncate to
  // current month so the YTD average reflects only elapsed time.
  const maxMonthForOccupancy = currentYear === today.getFullYear() ? today.getMonth() + 1 : 12;

  for (let m = 0; m < 12; m++) {
    const daysInMonth = new Date(currentYear, m + 1, 0).getDate();
    let monthOccDays = 0, monthAvailDays = 0, monthGrossRevGbp = 0, monthRevGbp = 0, monthNights = 0;
    let monthGrossRevUkGbp = 0, monthGrossRevFrEur = 0;
    let monthRevUkGbp = 0, monthRevFrEur = 0;
    // Stay-month accumulators for nightly rate (GBP-converted)
    let monthStayNightsTotal = 0, monthStayRevGbp = 0, monthAvailNightsTotal = 0;
    // Owned-only accumulators for headline RevPAR/ADR
    let monthOwnedStayNights = 0, monthOwnedStayRevGbp = 0, monthOwnedAvailNights = 0;

    for (const p of propertiesYtd) {
      const pm = p.monthly[m];
      const toGbp = p.currency === 'GBP' ? 1 : (p.currency === 'EUR' ? eurGbp : 0);
      const grossRevGbp = pm.gross_revenue * toGbp;
      const revGbp = pm.revenue * toGbp;
      const occDays = (pm.occupancy / 100) * daysInMonth;
      const pAvailNights = pm.available_nights || 0;

      monthGrossRevGbp += grossRevGbp;
      monthRevGbp += revGbp;
      monthNights += pm.nights;
      monthOccDays += occDays;
      monthAvailDays += daysInMonth;

      // Stay-month for nightly rate (GBP-converted)
      monthStayNightsTotal += pm.stay_nights;
      monthStayRevGbp += pm.stay_revenue * toGbp;
      monthAvailNightsTotal += pAvailNights;

      // Owned-only metrics for headline RevPAR/ADR
      const isOwned = p.management_type !== 'managed';
      if (isOwned) {
        monthOwnedStayNights += pm.stay_nights;
        monthOwnedStayRevGbp += pm.stay_revenue * toGbp;
        monthOwnedAvailNights += pAvailNights;
        // Per-region owned-only for rate card sub-lines
        if (p.region === 'uk') {
          ytdOwnedStayNightsUk += pm.stay_nights;
          ytdOwnedStayRevGbpUk += pm.stay_revenue * toGbp;
          ytdOwnedAvailNightsUk += pAvailNights;
        } else if (p.region === 'fr') {
          ytdOwnedStayNightsFr += pm.stay_nights;
          ytdOwnedStayRevGbpFr += pm.stay_revenue * toGbp;
          ytdOwnedAvailNightsFr += pAvailNights;
          ytdOwnedStayRevNativeFrEur += pm.stay_revenue;
        }
      }

      if (p.region === 'uk') {
        monthGrossRevUkGbp += pm.gross_revenue;
        monthRevUkGbp += pm.revenue;
      } else if (p.region === 'fr') {
        monthGrossRevFrEur += pm.gross_revenue;
        monthRevFrEur += pm.revenue;
      }

      // Per property, net margin = gross margin (no overheads of its own);
      // region overheads are taken off below.
      const pmGrossMargin = pm.net_margin;
      if (p.region === 'fr') {
        ytdGrossRevGbpFr += grossRevGbp;
        ytdRevGbpFr += revGbp;
        ytdGrossRevNativeFrEur += pm.gross_revenue;
        ytdRevNativeFrEur += pm.revenue;
        ytdNetMarginNativeFrEur += pm.net_margin;
        ytdNetMarginGbpFr += pm.net_margin * toGbp;
        ytdGrossMarginNativeFrEur += pmGrossMargin;
        ytdGrossMarginGbpFr += pmGrossMargin * toGbp;
        ytdNightsFr += pm.nights;
        ytdStayNightsFr += pm.stay_nights;
        ytdStayRevGbpFr += pm.stay_revenue * toGbp;
        ytdAvailNightsFr += pAvailNights;
      } else {
        ytdGrossRevGbpUk += grossRevGbp;
        ytdRevGbpUk += revGbp;
        ytdNetMarginGbpUk += pm.net_margin * toGbp;
        ytdGrossMarginGbpUk += pmGrossMargin * toGbp;
        ytdNightsUk += pm.nights;
        ytdStayNightsUk += pm.stay_nights;
        ytdStayRevGbpUk += pm.stay_revenue * toGbp;
        ytdAvailNightsUk += pAvailNights;
      }
      if (m + 1 <= maxMonthForOccupancy) {
        if (p.region === 'fr') {
          ytdOccDaysFr += occDays;
          ytdAvailDaysFr += daysInMonth;
        } else {
          ytdOccDaysUk += occDays;
          ytdAvailDaysUk += daysInMonth;
        }
      }
    }

    // Entity-level unattributed cost adjustments for this month (P&L parity).
    const mkAdj = `${currentYear}-${String(m + 1).padStart(2, '0')}`;
    for (const rk of ['uk', 'fr']) {
      const adjMc = mkAdj > currentMonthKeyUtc ? {} : ((regionCostAdj[rk] ?? {})[mkAdj] ?? {});
      const adjCostTotal = (adjMc.operational ?? 0) + (adjMc.management_fees ?? 0);
      // Company overheads of this region and month (net margin only).
      const ohM = mkAdj > currentMonthKeyUtc ? 0 : overheadOf(rk, mkAdj);
      if (rk === 'fr') {
        ytdNetMarginNativeFrEur -= ohM;
        ytdNetMarginGbpFr -= ohM * eurGbp;
      } else {
        ytdNetMarginGbpUk -= ohM;
      }
      if (adjCostTotal === 0) continue;
      if (rk === 'fr') {
        ytdNetMarginNativeFrEur -= adjCostTotal;
        ytdNetMarginGbpFr -= adjCostTotal * eurGbp;
        ytdGrossMarginNativeFrEur -= adjCostTotal;
        ytdGrossMarginGbpFr -= adjCostTotal * eurGbp;
      } else {
        ytdNetMarginGbpUk -= adjCostTotal;
        ytdGrossMarginGbpUk -= adjCostTotal;
      }
    }

    monthlyCombined.push({
      label: MONTHS[m],
      occupancy_pct: monthAvailDays > 0 ? (monthOccDays / monthAvailDays) * 100 : 0,
      avg_nightly_rate_gbp: monthStayNightsTotal > 0 ? monthStayRevGbp / monthStayNightsTotal : 0,
      revpar_gbp: monthAvailNightsTotal > 0 ? monthStayRevGbp / monthAvailNightsTotal : 0,
      revenue_gbp: monthRevGbp,
      gross_revenue_gbp: monthGrossRevGbp,
      nights: monthNights,
      revenue_uk_gbp: monthRevUkGbp,
      revenue_fr_eur: monthRevFrEur,
      gross_revenue_uk_gbp: monthGrossRevUkGbp,
      gross_revenue_fr_eur: monthGrossRevFrEur,
      stay_nights: monthStayNightsTotal,
      stay_revenue_gbp: monthStayRevGbp,
      available_nights: monthAvailNightsTotal,
    });

    ytdGrossRevGbpTotal += monthGrossRevGbp;
    ytdRevGbpTotal += monthRevGbp;
    ytdNightsTotal += monthNights;
    ytdStayNightsTotal += monthStayNightsTotal;
    ytdStayRevGbpTotal += monthStayRevGbp;
    ytdAvailNightsTotal += monthAvailNightsTotal;
    ytdOwnedStayNights += monthOwnedStayNights;
    ytdOwnedStayRevGbp += monthOwnedStayRevGbp;
    ytdOwnedAvailNights += monthOwnedAvailNights;
    if (m + 1 <= maxMonthForOccupancy) {
      ytdOccDaysTotal += monthOccDays;
      ytdAvailDaysTotal += monthAvailDays;
    }
  }

  const ytdCombined = {
    combined: {
      occupancy_pct: ytdAvailDaysTotal > 0 ? (ytdOccDaysTotal / ytdAvailDaysTotal) * 100 : 0,
      avg_nightly_rate_gbp: ytdStayNightsTotal > 0 ? ytdStayRevGbpTotal / ytdStayNightsTotal : 0,
      revpar_gbp: ytdAvailNightsTotal > 0 ? ytdStayRevGbpTotal / ytdAvailNightsTotal : 0,
      revenue_gbp: ytdRevGbpTotal,
      gross_revenue_gbp: ytdGrossRevGbpTotal,
      net_margin_gbp: ytdNetMarginGbpUk + ytdNetMarginGbpFr,
      gross_margin_gbp: ytdGrossMarginGbpUk + ytdGrossMarginGbpFr,
    },
    france: {
      occupancy_pct: ytdAvailDaysFr > 0 ? (ytdOccDaysFr / ytdAvailDaysFr) * 100 : 0,
      avg_nightly_rate_gbp: ytdStayNightsFr > 0 ? ytdStayRevGbpFr / ytdStayNightsFr : 0,
      revpar_gbp: ytdAvailNightsFr > 0 ? ytdStayRevGbpFr / ytdAvailNightsFr : 0,
      revenue_eur: ytdRevNativeFrEur,
      gross_revenue_eur: ytdGrossRevNativeFrEur,
      net_margin_eur: ytdNetMarginNativeFrEur,
      net_margin_gbp: ytdNetMarginGbpFr,
      gross_margin_eur: ytdGrossMarginNativeFrEur,
      gross_margin_gbp: ytdGrossMarginGbpFr,
    },
    uk: {
      occupancy_pct: ytdAvailDaysUk > 0 ? (ytdOccDaysUk / ytdAvailDaysUk) * 100 : 0,
      avg_nightly_rate_gbp: ytdStayNightsUk > 0 ? ytdStayRevGbpUk / ytdStayNightsUk : 0,
      revpar_gbp: ytdAvailNightsUk > 0 ? ytdStayRevGbpUk / ytdAvailNightsUk : 0,
      revenue_gbp: ytdRevGbpUk,
      gross_revenue_gbp: ytdGrossRevGbpUk,
      net_margin_gbp: ytdNetMarginGbpUk,
      gross_margin_gbp: ytdGrossMarginGbpUk,
    },
    // Owned-only headline metrics: managed properties excluded
    owned_only: {
      adr_gbp: ytdOwnedStayNights > 0 ? ytdOwnedStayRevGbp / ytdOwnedStayNights : 0,
      revpar_gbp: ytdOwnedAvailNights > 0 ? ytdOwnedStayRevGbp / ytdOwnedAvailNights : 0,
      occupancy_pct: ytdOwnedAvailNights > 0 ? (ytdOwnedStayNights / ytdOwnedAvailNights) * 100 : 0,
      // Per-region owned-only in native currency (survives currency conversion)
      adr_uk_gbp: ytdOwnedStayNightsUk > 0 ? ytdOwnedStayRevGbpUk / ytdOwnedStayNightsUk : 0,
      revpar_uk_gbp: ytdOwnedAvailNightsUk > 0 ? ytdOwnedStayRevGbpUk / ytdOwnedAvailNightsUk : 0,
      adr_fr_eur: ytdOwnedStayNightsFr > 0 ? ytdOwnedStayRevNativeFrEur / ytdOwnedStayNightsFr : 0,
      revpar_fr_eur: ytdOwnedAvailNightsFr > 0 ? ytdOwnedStayRevNativeFrEur / ytdOwnedAvailNightsFr : 0,
    },
  };

  // ── Totals (derived from per-property YTD, which itself was derived from monthly) ──
  const sumProps = (props, field) => props.reduce((s, p) => s + (p[field] || 0), 0);
  const ukProps = propertiesYtd.filter(p => p.region === 'uk');
  const frProps = propertiesYtd.filter(p => p.region === 'fr');

  const buildTotals = (props) => {
    const rev = sumProps(props, 'revenue_native');
    const netRev = sumProps(props, 'net_revenue_native');
    const grossRev = sumProps(props, 'gross_revenue_native');
    const ota = sumProps(props, 'ota_commission_native');
    const mgmtFees = sumProps(props, 'management_fees_native');
    const operational = sumProps(props, 'operational_native');
    const cleaningT = sumProps(props, 'cleaning_native');
    const repairsT = sumProps(props, 'repairs_native');
    const otherOpT = sumProps(props, 'other_operational_native');
    const financing = sumProps(props, 'financing_native');
    const unclassified = sumProps(props, 'unclassified_native');
    const totalCosts = sumProps(props, 'total_costs_native');
    const nm = sumProps(props, 'net_margin_native');
    const gm = nm; // per property, gross margin = net margin (overheads are applied per region below)
    const bk = sumProps(props, 'bookings_count');
    const ni = sumProps(props, 'nights');
    // Stay-month totals for nightly rate and RevPAR (sum across all per-property monthly)
    let stayNi = 0, stayRev = 0, availNi = 0;
    for (const p of props) {
      for (const pm of p.monthly) {
        stayNi += pm.stay_nights;
        stayRev += pm.stay_revenue;
        availNi += pm.available_nights || 0;
      }
    }
    return {
      revenue: rev,
      net_revenue: netRev,
      gross_revenue: grossRev,
      ota_commission: ota,
      management_fees: mgmtFees,
      operational,
      cleaning: cleaningT,
      repairs: repairsT,
      other_operational: otherOpT,
      financing,
      unclassified,
      total_costs: totalCosts,
      net_margin: nm,
      gross_margin: gm,
      margin_pct: grossRev > 0 ? ((nm / grossRev) * 100) : 0,
      gross_margin_pct: grossRev > 0 ? ((gm / grossRev) * 100) : 0,
      occupancy: props.length > 0 ? sumProps(props, 'occupancy_ytd_pct') / props.length : 0,
      avg_nightly_rate: stayNi > 0 ? stayRev / stayNi : 0,
      adr: stayNi > 0 ? stayRev / stayNi : 0,
      revpar: availNi > 0 ? stayRev / availNi : 0,
      bookings: bk,
      nights: ni,
      stay_nights: stayNi,
      stay_revenue: stayRev,
      available_nights: availNi,
      properties_count: props.length,
      // Native-currency snapshots for KPI sub-lines (survive currency conversion)
      revenue_native: rev,
      net_revenue_native: netRev,
      gross_revenue_native: grossRev,
      net_margin_native: nm,
      gross_margin_native: gm,
      avg_nightly_rate_native: stayNi > 0 ? stayRev / stayNi : 0,
      adr_native: stayNi > 0 ? stayRev / stayNi : 0,
      revpar_native: availNi > 0 ? stayRev / availNi : 0,
    };
  };

  const ukTotals = buildTotals(ukProps);
  const frTotals = buildTotals(frProps);
  Object.assign(ukTotals, ukActualTotals);
  Object.assign(frTotals, frActualTotals);

  // Apply entity-level unattributed cost adjustments to regional totals
  // (parity with P&L endpoint, which includes these in its region totals).
  for (const [rk, months] of Object.entries(regionCostAdj)) {
    const target = rk === 'uk' ? ukTotals : frTotals;
    for (const [adjMonth, adj] of Object.entries(months)) {
      if (adjMonth > currentMonthKeyUtc) continue;
      const adjOp = adj.operational ?? 0;
      const adjClean = adj.cleaning ?? 0;
      const adjMgmt = adj.management_fees ?? 0;
      const adjCostTotal = adjOp + adjMgmt;
      target.operational += adjOp;
      target.cleaning += adjClean;
      target.repairs = (target.repairs || 0) + (adj.repairs ?? 0);
      target.management_fees += adjMgmt;
      target.total_costs += adjCostTotal;
      // Net revenue is after management fees, cleaning and repairs (A148).
      const adjNetRev = adjMgmt + adjClean + (adj.repairs ?? 0);
      target.net_revenue -= adjNetRev;
      target.net_revenue_native -= adjNetRev;
      target.net_margin -= adjCostTotal;
      target.net_margin_native -= adjCostTotal;
      target.gross_margin -= adjCostTotal;
      target.gross_margin_native -= adjCostTotal;
    }
    if (target.gross_revenue > 0) {
      target.margin_pct = (target.net_margin / target.gross_revenue) * 100;
      target.gross_margin_pct = (target.gross_margin / target.gross_revenue) * 100;
    }
  }

  // Company overheads come off net margin per region (A148). Only months up
  // to the current one, like every other actual cost here.
  for (const [rk, target] of [['uk', ukTotals], ['fr', frTotals]]) {
    let oh = 0;
    for (const mk of Object.keys(overheadsByRegion[rk] || {})) {
      if (mk.slice(0, 4) !== String(currentYear) || mk > currentMonthKeyUtc) continue;
      oh += overheadOf(rk, mk);
    }
    target.overheads = oh;
    target.overheads_native = oh;
    target.net_margin -= oh;
    target.net_margin_native -= oh;
    target.margin_pct = target.gross_revenue > 0 ? (target.net_margin / target.gross_revenue) * 100 : 0;
  }

  const allProps = [...ukProps, ...frProps];
  const combinedRevGbp = ukTotals.revenue + frTotals.revenue * eurGbp;
  const combinedNetRevGbp = ukTotals.net_revenue + frTotals.net_revenue * eurGbp;
  const combinedGrossRevGbp = ukTotals.gross_revenue + frTotals.gross_revenue * eurGbp;
  const combinedNetMarginGbp = ukTotals.net_margin + frTotals.net_margin * eurGbp;
  const combinedGrossMarginGbp = ukTotals.gross_margin + frTotals.gross_margin * eurGbp;
  const combinedNights = ukTotals.nights + frTotals.nights;
  // Stay-month for combined nightly rate (GBP-converted)
  const combinedStayNights = ukTotals.stay_nights + frTotals.stay_nights;
  const combinedStayRevGbp = ukTotals.stay_revenue + frTotals.stay_revenue * eurGbp;
  const combinedAvailNights = ukTotals.available_nights + frTotals.available_nights;

  const combined = {
    revenue_gbp: combinedRevGbp,
    // Actuals-scoped YTD (dashboards round 2, "incl. to come" fix): summed
    // from ukTotals/frTotals' own actual fields (attached from
    // monthlyByRegion above), the SAME predicate the Gross Revenue chart's
    // YTD footer reads. Equal to the plain total for every month except the
    // current one, where a same-month forward-dated booking is excluded
    // (never an actual regardless of status).
    revenue_actual_gbp: ukTotals.revenue_actual + frTotals.revenue_actual * eurGbp,
    // Net revenue (A148 tier: after OTA, management fees, cleaning, repairs);
    // revenue_gbp above stays the after-OTA booking revenue.
    net_revenue_actual_gbp: ukTotals.net_revenue_actual + frTotals.net_revenue_actual * eurGbp,
    net_revenue_gbp: combinedNetRevGbp,
    overheads_gbp: ukTotals.overheads + frTotals.overheads * eurGbp,
    gross_revenue_gbp: combinedGrossRevGbp,
    gross_revenue_actual_gbp: ukTotals.gross_revenue_actual + frTotals.gross_revenue_actual * eurGbp,
    ota_commission_gbp: ukTotals.ota_commission + frTotals.ota_commission * eurGbp,
    management_fees_gbp: ukTotals.management_fees + frTotals.management_fees * eurGbp,
    operational_gbp: ukTotals.operational + frTotals.operational * eurGbp,
    cleaning_gbp: ukTotals.cleaning + frTotals.cleaning * eurGbp,
    repairs_gbp: ukTotals.repairs + frTotals.repairs * eurGbp,
    other_operational_gbp: ukTotals.other_operational + frTotals.other_operational * eurGbp,
    financing_gbp: ukTotals.financing + frTotals.financing * eurGbp,
    unclassified_gbp: ukTotals.unclassified + frTotals.unclassified * eurGbp,
    total_costs_gbp: ukTotals.total_costs + frTotals.total_costs * eurGbp,
    net_margin_gbp: combinedNetMarginGbp,
    gross_margin_gbp: combinedGrossMarginGbp,
    net_margin_actual_gbp: ukTotals.net_margin_actual + frTotals.net_margin_actual * eurGbp,
    gross_margin_actual_gbp: ukTotals.gross_margin_actual + frTotals.gross_margin_actual * eurGbp,
    margin_pct: combinedGrossRevGbp > 0 ? ((combinedNetMarginGbp / combinedGrossRevGbp) * 100) : 0,
    gross_margin_pct: combinedGrossRevGbp > 0 ? ((combinedGrossMarginGbp / combinedGrossRevGbp) * 100) : 0,
    occupancy: allProps.length > 0 ? sumProps(allProps, 'occupancy_ytd_pct') / allProps.length : 0,
    avg_nightly_rate_gbp: combinedStayNights > 0 ? combinedStayRevGbp / combinedStayNights : 0,
    revpar_gbp: combinedAvailNights > 0 ? combinedStayRevGbp / combinedAvailNights : 0,
    bookings: ukTotals.bookings + frTotals.bookings,
    nights: combinedNights,
    properties_count: allProps.length,
  };

  // ── Channel breakdown ──
  const buildChannelBreakdown = (filteredBookings, useGbp) => {
    const channelMap = {};
    for (const b of filteredBookings) {
      const ch = b.channel || 'other';
      if (!channelMap[ch]) channelMap[ch] = { channel: ch, revenue: 0, bookings: 0 };
      channelMap[ch].revenue += useGbp ? (b.revenue_gbp || 0) : (parseFloat(b.revenue) || 0);
      if (!b.is_cancelled) channelMap[ch].bookings += 1;
    }
    const entries = Object.values(channelMap).sort((a, b) => b.revenue - a.revenue);
    const total = entries.reduce((s, e) => s + e.revenue, 0);
    return entries.map(e => ({
      ...e,
      pct: total > 0 ? ((e.revenue / total) * 100) : 0,
    }));
  };

  const channelBreakdownByRegion = {
    uk: buildChannelBreakdown(bookings.filter(b => b.region === 'uk'), false),
    fr: buildChannelBreakdown(bookings.filter(b => b.region === 'fr'), false),
    all: buildChannelBreakdown(bookings, true),
  };

  // ── Per-month channel breakdown (GBP-converted, for monthly PDF reports) ──
  const channelBreakdownByMonth = {};
  for (let m = 1; m <= 12; m++) {
    const mk = `${currentYear}-${String(m).padStart(2, '0')}`;
    const monthBookings = bookings.filter(b => b.check_in_month === mk);
    channelBreakdownByMonth[mk] = buildChannelBreakdown(monthBookings, true);
  }

  return {
    bookings,
    propertiesYtd,
    monthlyByRegion,
    monthlyCombined,
    ytdCombined,
    totals: { uk: ukTotals, fr: frTotals, combined },
    channelBreakdownByRegion,
    channelBreakdownByMonth,
    fx: { EUR_GBP: eurGbp },
    displayCurrency: 'GBP',
    // Exposed for the Costs page overview (P&L-eligible vs full cash costs)
    costsByPropMonth,
    allCostsByPropMonth,
  };
}

/**
 * Bookings tab monthly rollup (single-region view), shared by the on-screen
 * table and its PDF/Excel exports so all three read the same figures.
 *
 * Display basis is guest-paid truth (display_gross/display_commission/
 * display_net, see the `bookings` enrichment above), never
 * gross_revenue/ota_commission -- for a MANAGED booking those are
 * classify_booking_pnl's fee-basis (management fee) P&L figures, a
 * different basis than what the guest paid / the management statement
 * calls gross. Each month's totals are the sum of its own row values by
 * construction (same reduce over the same filtered array), so a rollup
 * can never disagree with the rows it covers.
 */
export function buildBookingsTabMonthlyGroups(regionBookings, year, filters = {}) {
  const selectedProperties = filters.properties || [];
  const selectedChannels = filters.channels || [];

  let filtered = regionBookings;
  if (selectedProperties.length > 0) {
    filtered = filtered.filter(b => selectedProperties.includes(b.property_name));
  }
  if (selectedChannels.length > 0) {
    filtered = filtered.filter(b => selectedChannels.includes(b.channel || 'other'));
  }

  const groups = [];
  for (let m = 1; m <= 12; m++) {
    const mk = `${year}-${String(m).padStart(2, '0')}`;
    const monthBookings = filtered
      .filter(b => b.check_in_month === mk)
      .sort((a, b) => (a.check_in || '').localeCompare(b.check_in || ''));

    const gross = monthBookings.reduce((s, b) => s + (b.display_gross || 0), 0);
    const otaCommission = monthBookings.reduce((s, b) => s + (b.display_commission || 0), 0);
    const netRevenue = monthBookings.reduce((s, b) => s + (b.display_net || 0), 0);
    const nights = monthBookings.reduce((s, b) => s + (b.nights || 0), 0);
    // A row with no resolvable guest total contributes 0 to gross/
    // otaCommission/netRevenue above (never a fee-basis or statement
    // substitute -- see display_gross). That 0 would otherwise silently
    // understate the rollup with no visible trace, so count it here and
    // surface it next to the month/total figures (CLAUDE.md "no silent
    // caps" -- an incomplete sum must say so).
    const unavailableGuestTotalCount = monthBookings.filter(b => !b.is_cancelled && b.display_gross == null).length;

    groups.push({
      month: m, monthKey: mk, label: MONTHS[m - 1],
      // A cancelled booking (even a paid one, whose fee-only revenue still
      // flows through gross/netRevenue above) never stayed and must not
      // count as a booking.
      bookings: monthBookings, count: monthBookings.filter(b => !b.is_cancelled).length,
      nights, gross, otaCommission, netRevenue, unavailableGuestTotalCount,
    });
  }

  const totals = {
    count: groups.reduce((s, g) => s + g.count, 0),
    nights: groups.reduce((s, g) => s + g.nights, 0),
    gross: groups.reduce((s, g) => s + g.gross, 0),
    otaCommission: groups.reduce((s, g) => s + g.otaCommission, 0),
    netRevenue: groups.reduce((s, g) => s + g.netRevenue, 0),
    unavailableGuestTotalCount: groups.reduce((s, g) => s + g.unavailableGuestTotalCount, 0),
  };

  return { monthlyGroups: groups, totals };
}

/**
 * Pure post-processor: converts all monetary values in a derived-metrics
 * object to `targetCurrency`.
 *
 * computeDerivedMetrics outputs per-property and per-region-total values in
 * NATIVE currency (EUR for FR, GBP for UK). Cross-region aggregates
 * (monthlyByRegion._gbp siblings, ytdCombined, totals.combined) are in GBP.
 * This function normalises everything to the requested display currency.
 *
 * When targetCurrency === 'GBP':
 *   - Native-GBP values (UK) pass through unchanged
 *   - Native-EUR values (FR) per-property and per-region totals are
 *     converted to GBP via EUR_GBP
 *   - monthlyByRegion, monthlyCombined, ytdCombined already carry correct
 *     _gbp keys from computeDerivedMetrics and are passed through
 *
 * When targetCurrency === 'EUR':
 *   - Native-EUR values (FR) pass through unchanged
 *   - Native-GBP values (UK) are converted to EUR via 1/EUR_GBP
 *   - Combined/cross-region totals are recomputed in EUR
 *
 * The function is idempotent: it always converts from the native-currency
 * base, never from a previously-converted result.
 */
export function convertDerivedToDisplayCurrency(derived, targetCurrency) {
  if (!derived) return null;

  if (targetCurrency === 'GBP') {
    const eurGbp = derived.fx.EUR_GBP;
    if (!eurGbp || eurGbp <= 0) return { ...derived, displayCurrency: 'GBP' };

    // Multiplier via shared helper
    const mx = (nativeCurrency) => getConversionRate(nativeCurrency, 'GBP', eurGbp);

    // ── Per-property: convert monthly + YTD ──
    const propertiesYtd = derived.propertiesYtd.map(p => {
      const rate = mx(p.currency);
      const monthly = p.monthly.map(m => ({
        ...m,
        revenue: m.revenue * rate,
        net_revenue: m.net_revenue * rate,
        gross_revenue: m.gross_revenue * rate,
        ota_commission: m.ota_commission * rate,
        management_fees: m.management_fees * rate,
        operational: m.operational * rate,
        cleaning: (m.cleaning || 0) * rate,
        repairs: (m.repairs || 0) * rate,
        other_operational: (m.other_operational || 0) * rate,
        financing: m.financing * rate,
        unclassified: m.unclassified * rate,
        total_costs: m.total_costs * rate,
        net_margin: m.net_margin * rate,
        gross_margin: (m.gross_margin || 0) * rate,
        stay_revenue: m.stay_revenue * rate,
        avg_nightly_rate: m.stay_nights > 0 ? (m.stay_revenue * rate) / m.stay_nights : 0,
        adr: m.stay_nights > 0 ? (m.stay_revenue * rate) / m.stay_nights : 0,
        revpar: m.available_nights > 0 ? (m.stay_revenue * rate) / m.available_nights : 0,
      }));
      return {
        ...p,
        revenue_native: p.revenue_native * rate,
        net_revenue_native: p.net_revenue_native * rate,
        gross_revenue_native: p.gross_revenue_native * rate,
        ota_commission_native: p.ota_commission_native * rate,
        management_fees_native: p.management_fees_native * rate,
        operational_native: p.operational_native * rate,
        cleaning_native: (p.cleaning_native || 0) * rate,
        repairs_native: (p.repairs_native || 0) * rate,
        other_operational_native: (p.other_operational_native || 0) * rate,
        financing_native: p.financing_native * rate,
        unclassified_native: p.unclassified_native * rate,
        total_costs_native: p.total_costs_native * rate,
        net_margin_native: p.net_margin_native * rate,
        gross_margin_native: (p.gross_margin_native || 0) * rate,
        avg_nightly_rate_native: p.avg_nightly_rate_native * rate,
        adr_native: p.adr_native * rate,
        revpar_native: p.revpar_native * rate,
        investment_native: p.investment_native != null ? p.investment_native * mx(p.investment_currency || p.currency) : null,
        investment_currency: 'GBP',
        monthly,
      };
    });

    // ── Totals ──
    const convertRegionTotals = (t, nativeCurrency) => {
      const rate = mx(nativeCurrency);
      const convertedStayRev = (t.stay_revenue || 0) * rate;
      const stayNi = t.stay_nights || 0;
      const availNi = t.available_nights || 0;
      return {
        ...t,
        revenue: (t.revenue || 0) * rate,
        net_revenue: (t.net_revenue || 0) * rate,
        gross_revenue: (t.gross_revenue || 0) * rate,
        ota_commission: (t.ota_commission || 0) * rate,
        management_fees: (t.management_fees || 0) * rate,
        operational: (t.operational || 0) * rate,
        cleaning: (t.cleaning || 0) * rate,
        repairs: (t.repairs || 0) * rate,
        other_operational: (t.other_operational || 0) * rate,
        financing: (t.financing || 0) * rate,
        unclassified: (t.unclassified || 0) * rate,
        total_costs: (t.total_costs || 0) * rate,
        net_margin: t.net_margin * rate,
        gross_margin: (t.gross_margin || 0) * rate,
        // Actuals-scoped YTD (dashboards round 2, PropertyPortfolioPage
        // follow-up): converted the same way as their full-total siblings
        // above, native rate -> target, never a GBP round-trip.
        revenue_actual: (t.revenue_actual || 0) * rate,
        net_revenue_actual: (t.net_revenue_actual || 0) * rate,
        overheads: (t.overheads || 0) * rate,
        gross_revenue_actual: (t.gross_revenue_actual || 0) * rate,
        net_margin_actual: (t.net_margin_actual || 0) * rate,
        gross_margin_actual: (t.gross_margin_actual || 0) * rate,
        stay_revenue: convertedStayRev,
        avg_nightly_rate: stayNi > 0 ? convertedStayRev / stayNi : 0,
        adr: stayNi > 0 ? convertedStayRev / stayNi : 0,
        revpar: availNi > 0 ? convertedStayRev / availNi : 0,
      };
    };
    const frTotals = convertRegionTotals(derived.totals.fr, 'EUR');

    // ── Channel breakdown: convert FR channel revenues to GBP ──
    const channelBreakdownByRegion = {
      ...derived.channelBreakdownByRegion,
      fr: derived.channelBreakdownByRegion.fr.map(e => ({
        ...e,
        revenue: e.revenue * eurGbp,
      })),
    };

    // monthlyByRegion, monthlyCombined, ytdCombined, bookings already have
    // correct GBP values from computeDerivedMetrics (via _gbp sibling keys
    // and per-booking revenue_gbp).
    return {
      ...derived,
      propertiesYtd,
      totals: { uk: derived.totals.uk, fr: frTotals, combined: derived.totals.combined },
      channelBreakdownByRegion,
      displayCurrency: 'GBP',
    };
  }

  const eurGbp = derived.fx.EUR_GBP;
  if (!eurGbp || eurGbp <= 0) return { ...derived, displayCurrency: targetCurrency };
  const gbpToEur = 1 / eurGbp;

  // Multiplier via shared helper
  const mx = (nativeCurrency) => getConversionRate(nativeCurrency, targetCurrency, eurGbp);

  // ── Bookings ──
  const bookings = derived.bookings.map(b => {
    const rate = mx(b.currency);
    return {
      ...b,
      revenue_gbp: (parseFloat(b.revenue) || 0) * rate,
    };
  });

  // ── Per-property: convert monthly + YTD ──
  const propertiesYtd = derived.propertiesYtd.map(p => {
    const rate = mx(p.currency);
    const monthly = p.monthly.map(m => ({
      ...m,
      revenue: m.revenue * rate,
      net_revenue: m.net_revenue * rate,
      gross_revenue: m.gross_revenue * rate,
      ota_commission: m.ota_commission * rate,
      management_fees: m.management_fees * rate,
      operational: m.operational * rate,
      cleaning: (m.cleaning || 0) * rate,
      repairs: (m.repairs || 0) * rate,
      other_operational: (m.other_operational || 0) * rate,
      financing: m.financing * rate,
      unclassified: m.unclassified * rate,
      total_costs: m.total_costs * rate,
      net_margin: m.net_margin * rate,
      gross_margin: (m.gross_margin || 0) * rate,
      // margin_pct is dimensionless, unchanged
      stay_revenue: m.stay_revenue * rate,
      // stay_nights is dimensionless, unchanged
      avg_nightly_rate: m.stay_nights > 0 ? (m.stay_revenue * rate) / m.stay_nights : 0,
      adr: m.stay_nights > 0 ? (m.stay_revenue * rate) / m.stay_nights : 0,
      revpar: m.available_nights > 0 ? (m.stay_revenue * rate) / m.available_nights : 0,
    }));
    return {
      ...p,
      revenue_native: p.revenue_native * rate,
      net_revenue_native: p.net_revenue_native * rate,
      gross_revenue_native: p.gross_revenue_native * rate,
      ota_commission_native: p.ota_commission_native * rate,
      management_fees_native: p.management_fees_native * rate,
      operational_native: p.operational_native * rate,
      cleaning_native: (p.cleaning_native || 0) * rate,
      repairs_native: (p.repairs_native || 0) * rate,
      other_operational_native: (p.other_operational_native || 0) * rate,
      financing_native: p.financing_native * rate,
      unclassified_native: p.unclassified_native * rate,
      total_costs_native: p.total_costs_native * rate,
      net_margin_native: p.net_margin_native * rate,
      gross_margin_native: (p.gross_margin_native || 0) * rate,
      avg_nightly_rate_native: p.avg_nightly_rate_native * rate,
      adr_native: p.adr_native * rate,
      revpar_native: p.revpar_native * rate,
      investment_native: p.investment_native != null ? p.investment_native * mx(p.investment_currency || p.currency) : null,
      investment_currency: targetCurrency,
      monthly,
    };
  });

  // ── Monthly by region ──
  const monthlyByRegion = {
    uk: derived.monthlyByRegion.uk.map(m => {
      const rate = gbpToEur;
      return {
        ...m,
        revenue_eur: (m.revenue_gbp || 0) * rate,
        net_revenue_eur: (m.net_revenue_gbp || 0) * rate,
        net_revenue_actual_eur: (m.net_revenue_actual_gbp || 0) * rate,
        overheads_eur: (m.overheads_gbp || 0) * rate,
        revenue_actual_eur: (m.revenue_actual_gbp || 0) * rate,
        revenue_forward_eur: (m.revenue_forward_gbp || 0) * rate,
        gross_revenue_eur: (m.gross_revenue_gbp || 0) * rate,
        gross_revenue_actual_eur: (m.gross_revenue_actual_gbp || 0) * rate,
        gross_revenue_forward_eur: (m.gross_revenue_forward_gbp || 0) * rate,
        ota_commission_eur: (m.ota_commission_gbp || 0) * rate,
        management_fees_eur: (m.management_fees_gbp || 0) * rate,
        operational_eur: (m.operational_gbp || 0) * rate,
        cleaning_eur: (m.cleaning_gbp || 0) * rate,
        repairs_eur: (m.repairs_gbp || 0) * rate,
        other_operational_eur: (m.other_operational_gbp || 0) * rate,
        financing_eur: (m.financing_gbp || 0) * rate,
        unclassified_eur: (m.unclassified_gbp || 0) * rate,
        total_costs_eur: (m.total_costs_gbp || 0) * rate,
        net_margin_eur: (m.net_margin_gbp || 0) * rate,
        gross_margin_eur: (m.gross_margin_gbp || 0) * rate,
        net_margin_actual_eur: (m.net_margin_actual_gbp || 0) * rate,
        gross_margin_actual_eur: (m.gross_margin_actual_gbp || 0) * rate,
        avg_nightly_rate_eur: m.stay_nights > 0 ? (m.stay_revenue * rate) / m.stay_nights : 0,
        revpar_eur: m.available_nights > 0 ? (m.stay_revenue * rate) / m.available_nights : 0,
        stay_revenue: m.stay_revenue * rate,
        // Remove GBP keys so consumers use the EUR ones
        revenue_gbp: undefined,
        net_revenue_gbp: undefined,
        net_revenue_actual_gbp: undefined,
        overheads_gbp: undefined,
        revenue_actual_gbp: undefined,
        revenue_forward_gbp: undefined,
        gross_revenue_gbp: undefined,
        gross_revenue_actual_gbp: undefined,
        gross_revenue_forward_gbp: undefined,
        ota_commission_gbp: undefined,
        management_fees_gbp: undefined,
        operational_gbp: undefined,
        cleaning_gbp: undefined,
        repairs_gbp: undefined,
        other_operational_gbp: undefined,
        financing_gbp: undefined,
        unclassified_gbp: undefined,
        total_costs_gbp: undefined,
        net_margin_gbp: undefined,
        gross_margin_gbp: undefined,
        net_margin_actual_gbp: undefined,
        gross_margin_actual_gbp: undefined,
        avg_nightly_rate_gbp: undefined,
        revpar_gbp: undefined,
      };
    }),
    fr: derived.monthlyByRegion.fr.map(m => ({
      ...m,
      // Remove GBP keys so consumers use the EUR ones
      revenue_gbp: undefined,
      net_revenue_gbp: undefined,
      net_revenue_actual_gbp: undefined,
      overheads_gbp: undefined,
      revenue_actual_gbp: undefined,
      revenue_forward_gbp: undefined,
      gross_revenue_gbp: undefined,
      gross_revenue_actual_gbp: undefined,
      gross_revenue_forward_gbp: undefined,
      ota_commission_gbp: undefined,
      management_fees_gbp: undefined,
      operational_gbp: undefined,
      cleaning_gbp: undefined,
      repairs_gbp: undefined,
      other_operational_gbp: undefined,
      financing_gbp: undefined,
      unclassified_gbp: undefined,
      total_costs_gbp: undefined,
      net_margin_gbp: undefined,
      gross_margin_gbp: undefined,
      net_margin_actual_gbp: undefined,
      gross_margin_actual_gbp: undefined,
      avg_nightly_rate_gbp: undefined,
      revpar_gbp: undefined,
    })),
  };

  // ── Monthly combined ──
  const monthlyCombined = derived.monthlyCombined.map((m, i) => {
    const ukRev = monthlyByRegion.uk[i].revenue_eur || 0;
    const frRev = monthlyByRegion.fr[i].revenue_eur || 0;
    const ukGrossRev = monthlyByRegion.uk[i].gross_revenue_eur || 0;
    const frGrossRev = monthlyByRegion.fr[i].gross_revenue_eur || 0;
    const totalGrossRev = ukGrossRev + frGrossRev;
    // Stay-month for nightly rate: convert to EUR
    const ukStayRev = (monthlyByRegion.uk[i].stay_revenue || 0);
    const frStayRev = (monthlyByRegion.fr[i].stay_revenue || 0);
    const totalStayNights = (monthlyByRegion.uk[i].stay_nights || 0) + (monthlyByRegion.fr[i].stay_nights || 0);
    const totalStayRev = ukStayRev + frStayRev;
    const totalAvailNights = (monthlyByRegion.uk[i].available_nights || 0) + (monthlyByRegion.fr[i].available_nights || 0);
    return {
      ...m,
      revenue_gbp: ukRev + frRev, // field name kept for compatibility; value is in display currency
      gross_revenue_gbp: totalGrossRev, // field name kept for compatibility; value is in display currency
      avg_nightly_rate_gbp: totalStayNights > 0 ? totalStayRev / totalStayNights : 0,
      revpar_gbp: totalAvailNights > 0 ? totalStayRev / totalAvailNights : 0,
      revenue_uk_gbp: (m.revenue_uk_gbp || 0) * gbpToEur,
      gross_revenue_uk_gbp: (m.gross_revenue_uk_gbp || 0) * gbpToEur,
      stay_nights: totalStayNights,
      stay_revenue_gbp: totalStayRev,
      available_nights: totalAvailNights,
      // FR EUR passes through
    };
  });

  // ── YTD combined ──
  // Recompute nightly rate from monthlyCombined stay data (already EUR-converted above)
  let ytdStayNiEur = 0, ytdStayRevEur = 0, ytdAvailNiEur = 0;
  let ytdStayNiFrEur = 0, ytdStayRevFrEur = 0, ytdAvailNiFrEur = 0;
  let ytdStayNiUkEur = 0, ytdStayRevUkEur = 0, ytdAvailNiUkEur = 0;
  for (let i = 0; i < 12; i++) {
    const ukM = monthlyByRegion.uk[i];
    const frM = monthlyByRegion.fr[i];
    ytdStayNiEur += (ukM.stay_nights || 0) + (frM.stay_nights || 0);
    ytdStayRevEur += (ukM.stay_revenue || 0) + (frM.stay_revenue || 0);
    ytdAvailNiEur += (ukM.available_nights || 0) + (frM.available_nights || 0);
    ytdStayNiFrEur += (frM.stay_nights || 0);
    ytdStayRevFrEur += (frM.stay_revenue || 0);
    ytdAvailNiFrEur += (frM.available_nights || 0);
    ytdStayNiUkEur += (ukM.stay_nights || 0);
    ytdStayRevUkEur += (ukM.stay_revenue || 0);
    ytdAvailNiUkEur += (ukM.available_nights || 0);
  }
  const ytdCombined = {
    combined: {
      ...derived.ytdCombined.combined,
      avg_nightly_rate_gbp: ytdStayNiEur > 0 ? ytdStayRevEur / ytdStayNiEur : 0,
      revpar_gbp: ytdAvailNiEur > 0 ? ytdStayRevEur / ytdAvailNiEur : 0,
      revenue_gbp: (derived.ytdCombined.combined.revenue_gbp || 0) * gbpToEur,
      gross_revenue_gbp: (derived.ytdCombined.combined.gross_revenue_gbp || 0) * gbpToEur,
      net_margin_gbp: (derived.ytdCombined.combined.net_margin_gbp || 0) * gbpToEur,
      gross_margin_gbp: (derived.ytdCombined.combined.gross_margin_gbp || 0) * gbpToEur,
    },
    france: {
      ...derived.ytdCombined.france,
      avg_nightly_rate_gbp: ytdStayNiFrEur > 0 ? ytdStayRevFrEur / ytdStayNiFrEur : 0,
      revpar_gbp: ytdAvailNiFrEur > 0 ? ytdStayRevFrEur / ytdAvailNiFrEur : 0,
    },
    uk: {
      ...derived.ytdCombined.uk,
      avg_nightly_rate_gbp: ytdStayNiUkEur > 0 ? ytdStayRevUkEur / ytdStayNiUkEur : 0,
      revpar_gbp: ytdAvailNiUkEur > 0 ? ytdStayRevUkEur / ytdAvailNiUkEur : 0,
    },
    owned_only: derived.ytdCombined.owned_only ? {
      ...derived.ytdCombined.owned_only,
      adr_gbp: derived.ytdCombined.owned_only.adr_gbp * gbpToEur,
      revpar_gbp: derived.ytdCombined.owned_only.revpar_gbp * gbpToEur,
    } : undefined,
  };

  // ── Totals ──
  const convertRegionTotals = (t, nativeCurrency) => {
    const rate = mx(nativeCurrency);
    const convertedStayRev = (t.stay_revenue || 0) * rate;
    const stayNi = t.stay_nights || 0;
    const availNi = t.available_nights || 0;
    return {
      ...t,
      revenue: (t.revenue || 0) * rate,
      net_revenue: (t.net_revenue || 0) * rate,
      gross_revenue: (t.gross_revenue || 0) * rate,
      ota_commission: (t.ota_commission || 0) * rate,
      management_fees: (t.management_fees || 0) * rate,
      operational: (t.operational || 0) * rate,
      cleaning: (t.cleaning || 0) * rate,
      repairs: (t.repairs || 0) * rate,
      other_operational: (t.other_operational || 0) * rate,
      financing: (t.financing || 0) * rate,
      unclassified: (t.unclassified || 0) * rate,
      total_costs: (t.total_costs || 0) * rate,
      net_margin: t.net_margin * rate,
      gross_margin: (t.gross_margin || 0) * rate,
      // Actuals-scoped YTD (dashboards round 2, "incl. to come" fix):
      // converted the same way as their full-total siblings above, native
      // rate -> target, never a GBP round-trip.
      revenue_actual: (t.revenue_actual || 0) * rate,
      net_revenue_actual: (t.net_revenue_actual || 0) * rate,
      overheads: (t.overheads || 0) * rate,
      gross_revenue_actual: (t.gross_revenue_actual || 0) * rate,
      net_margin_actual: (t.net_margin_actual || 0) * rate,
      gross_margin_actual: (t.gross_margin_actual || 0) * rate,
      stay_revenue: convertedStayRev,
      avg_nightly_rate: stayNi > 0 ? convertedStayRev / stayNi : 0,
      adr: stayNi > 0 ? convertedStayRev / stayNi : 0,
      revpar: availNi > 0 ? convertedStayRev / availNi : 0,
      // margin_pct, gross_margin_pct, occupancy, bookings, nights, stay_nights, properties_count unchanged
    };
  };

  const ukTotals = convertRegionTotals(derived.totals.uk, 'GBP');
  const frTotals = convertRegionTotals(derived.totals.fr, 'EUR');
  const combinedRev = (ukTotals.revenue || 0) + (frTotals.revenue || 0);
  const combinedNetRev = (ukTotals.net_revenue || 0) + (frTotals.net_revenue || 0);
  const combinedGrossRev = (ukTotals.gross_revenue || 0) + (frTotals.gross_revenue || 0);
  const combinedNm = ukTotals.net_margin + frTotals.net_margin;
  const combinedGm = (ukTotals.gross_margin || 0) + (frTotals.gross_margin || 0);
  const combinedStayNi = (ukTotals.stay_nights || 0) + (frTotals.stay_nights || 0);
  const combinedStayRev = (ukTotals.stay_revenue || 0) + (frTotals.stay_revenue || 0);
  const combinedAvailNi = (ukTotals.available_nights || 0) + (frTotals.available_nights || 0);
  const combined = {
    ...derived.totals.combined,
    revenue_gbp: combinedRev,
    revenue_actual_gbp: (ukTotals.revenue_actual || 0) + (frTotals.revenue_actual || 0),
    net_revenue_actual_gbp: (ukTotals.net_revenue_actual || 0) + (frTotals.net_revenue_actual || 0),
    net_revenue_gbp: combinedNetRev,
    overheads_gbp: (ukTotals.overheads || 0) + (frTotals.overheads || 0),
    gross_revenue_gbp: combinedGrossRev,
    gross_revenue_actual_gbp: (ukTotals.gross_revenue_actual || 0) + (frTotals.gross_revenue_actual || 0),
    ota_commission_gbp: (ukTotals.ota_commission || 0) + (frTotals.ota_commission || 0),
    management_fees_gbp: (ukTotals.management_fees || 0) + (frTotals.management_fees || 0),
    operational_gbp: (ukTotals.operational || 0) + (frTotals.operational || 0),
    cleaning_gbp: (ukTotals.cleaning || 0) + (frTotals.cleaning || 0),
    repairs_gbp: (ukTotals.repairs || 0) + (frTotals.repairs || 0),
    other_operational_gbp: (ukTotals.other_operational || 0) + (frTotals.other_operational || 0),
    financing_gbp: (ukTotals.financing || 0) + (frTotals.financing || 0),
    unclassified_gbp: (ukTotals.unclassified || 0) + (frTotals.unclassified || 0),
    total_costs_gbp: (ukTotals.total_costs || 0) + (frTotals.total_costs || 0),
    net_margin_gbp: combinedNm,
    gross_margin_gbp: combinedGm,
    net_margin_actual_gbp: (ukTotals.net_margin_actual || 0) + (frTotals.net_margin_actual || 0),
    gross_margin_actual_gbp: (ukTotals.gross_margin_actual || 0) + (frTotals.gross_margin_actual || 0),
    margin_pct: combinedGrossRev > 0 ? ((combinedNm / combinedGrossRev) * 100) : 0,
    gross_margin_pct: combinedGrossRev > 0 ? ((combinedGm / combinedGrossRev) * 100) : 0,
    avg_nightly_rate_gbp: combinedStayNi > 0 ? combinedStayRev / combinedStayNi : 0,
    revpar_gbp: combinedAvailNi > 0 ? combinedStayRev / combinedAvailNi : 0,
  };

  // ── Channel breakdown ──
  const convertChannels = (entries, nativeCurrency) => {
    const rate = mx(nativeCurrency);
    const converted = entries.map(e => ({ ...e, revenue: e.revenue * rate }));
    // pct is relative, unchanged
    return converted;
  };
  const channelBreakdownByRegion = {
    uk: convertChannels(derived.channelBreakdownByRegion.uk, 'GBP'),
    fr: derived.channelBreakdownByRegion.fr.map(e => ({ ...e })), // already EUR
    all: (() => {
      // Rebuild from converted bookings
      const channelMap = {};
      for (const b of bookings) {
        const ch = b.channel || 'other';
        if (!channelMap[ch]) channelMap[ch] = { channel: ch, revenue: 0, bookings: 0 };
        channelMap[ch].revenue += b.revenue_gbp || 0; // now in EUR
        if (!b.is_cancelled) channelMap[ch].bookings += 1;
      }
      const entries = Object.values(channelMap).sort((a, b) => b.revenue - a.revenue);
      const total = entries.reduce((s, e) => s + e.revenue, 0);
      return entries.map(e => ({ ...e, pct: total > 0 ? ((e.revenue / total) * 100) : 0 }));
    })(),
  };

  return {
    bookings,
    propertiesYtd,
    monthlyByRegion,
    monthlyCombined,
    ytdCombined,
    totals: { uk: ukTotals, fr: frTotals, combined },
    channelBreakdownByRegion,
    channelBreakdownByMonth: derived.channelBreakdownByMonth,
    fx: derived.fx,
    displayCurrency: targetCurrency,
    costsByPropMonth: derived.costsByPropMonth,
    allCostsByPropMonth: derived.allCostsByPropMonth,
  };
}

/**
 * Drop the actuals-to-today / forward split fields (`*_actual`, `*_forward`,
 * with or without a currency suffix) so every consumer falls back to the
 * whole-month figures. One basis everywhere (Fan, 2026-10-25: "All numbers
 * should be the same basis"): the current month counts in full, as on the
 * P&L, Portfolio dashboard and Reporting pages; later months are the
 * forecast. Used by the Overall dashboard, whose builders still read the
 * split fields when present. Returns a shallow copy; arrays are mapped.
 */
const SPLIT_KEY_RE = /_(actual|forward)(_gbp|_eur)?$/;
export function wholeMonthBasis(value) {
  if (Array.isArray(value)) return value.map(wholeMonthBasis);
  if (!value || typeof value !== 'object') return value;
  const out = {};
  for (const [k, v] of Object.entries(value)) {
    if (SPLIT_KEY_RE.test(k)) continue;
    out[k] = v;
  }
  return out;
}
